/**
 * MunicipioDashboard · Web
 *
 * Dashboard dedicado para contas com role="municipio" (câmaras municipais)
 * e opcionalmente admin/staff com `municipality_id` associado.
 *
 * Mostra:
 *   - Header com nome do município + logout
 *   - Filtros de período: 7d / 30d / 90d / All-time
 *   - Cards de KPIs: rotas ligadas, KM totais, utilizadores, taxa de conclusão
 *   - Tabela: rotas do município com stats por rota (starts / completes / km)
 *   - Top 10 POIs mais clicados
 *   - Gráfico (30d) de starts vs completes por dia (SVG puro — sem libs extra)
 *
 * Design: paleta Rugged do LoginPage (bg #2B2A25, brand #E87A2A, sand #C4A275).
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Building2, TrendingUp, MapPin, Users, Route as RouteIcon,
  BarChart3, Loader2, Flag, RefreshCw, Eye, FileSpreadsheet, ArrowLeft, ExternalLink, Link2,
} from 'lucide-react';
import RouteViewerModal from '@/components/RouteViewerModal';
import EmbedSnippetCard from '@/components/EmbedSnippetCard';
import AlertDetailDialog from '@/components/AlertDetailDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Paleta Rugged
const C = {
  bg: '#2B2A25',
  bg2: '#241E19',
  bg3: '#0F0D0B',
  brand: '#E87A2A',
  brandDim: 'rgba(232,122,42,0.16)',
  sand: '#C4A275',
  sandLine: 'rgba(196,162,117,0.18)',
  ink: '#F2EBDD',
  inkDim: '#8A7A63',
  cardBg: 'rgba(26,22,19,0.82)',
  green: '#3FA34D',
  greenDim: 'rgba(63,163,77,0.16)',
};

const PERIODS = [
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
  { id: '90d', label: '90d' },
  { id: 'all', label: 'Sempre' },
];

const KpiCard = ({ icon: Icon, label, value, sub, tint = C.brand }) => (
  <div
    className="rounded-xl p-4 border"
    style={{ backgroundColor: C.cardBg, borderColor: C.sandLine }}
    data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-center gap-2 mb-2">
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: 'rgba(232,122,42,0.18)' }}
      >
        <Icon className="w-5 h-5" style={{ color: tint }} />
      </div>
      <div className="text-xs uppercase tracking-wider font-bold" style={{ color: C.inkDim }}>
        {label}
      </div>
    </div>
    <div className="text-3xl font-bold" style={{ color: C.ink }}>{value}</div>
    {sub && <div className="text-xs mt-1" style={{ color: C.inkDim }}>{sub}</div>}
  </div>
);

/** Mini timeseries (SVG) — chart 30d de starts/completes/km. */
const MiniChart = ({ data }) => {
  const width = 720;
  const height = 220;
  const pad = { top: 16, right: 16, bottom: 24, left: 32 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;

  if (!data || data.length === 0) {
    return (
      <div className="text-sm text-center py-8" style={{ color: C.inkDim }}>
        Sem dados nos últimos 30 dias
      </div>
    );
  }

  const maxY = Math.max(1, ...data.map((d) => Math.max(d.starts, d.completes)));
  const xStep = data.length > 1 ? innerW / (data.length - 1) : 0;
  const toXY = (v, i) => [pad.left + i * xStep, pad.top + innerH - (v / maxY) * innerH];

  const line = (key, color) => {
    const pts = data.map((d, i) => toXY(d[key], i).join(',')).join(' ');
    return <polyline points={pts} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />;
  };

  return (
    <div className="overflow-x-auto">
      <svg width={width} height={height} className="min-w-[600px]">
        {/* Grid horizontais */}
        {[0, 0.25, 0.5, 0.75, 1].map((f) => (
          <line
            key={f}
            x1={pad.left} x2={pad.left + innerW}
            y1={pad.top + innerH * f} y2={pad.top + innerH * f}
            stroke={C.sandLine}
          />
        ))}
        {line('starts', C.brand)}
        {line('completes', C.green)}
        {/* Y ticks */}
        <text x={pad.left - 6} y={pad.top + 4} fontSize="10" textAnchor="end" fill={C.inkDim}>{maxY}</text>
        <text x={pad.left - 6} y={pad.top + innerH + 4} fontSize="10" textAnchor="end" fill={C.inkDim}>0</text>
        {/* X ticks: primeiro e último */}
        {data[0] && (
          <text x={pad.left} y={height - 6} fontSize="10" fill={C.inkDim}>
            {data[0].date.slice(5)}
          </text>
        )}
        {data[data.length - 1] && (
          <text x={pad.left + innerW} y={height - 6} fontSize="10" textAnchor="end" fill={C.inkDim}>
            {data[data.length - 1].date.slice(5)}
          </text>
        )}
      </svg>
      <div className="flex gap-4 mt-2 text-xs" style={{ color: C.inkDim }}>
        <span><span className="inline-block w-3 h-0.5 mr-1 align-middle" style={{ backgroundColor: C.brand }} /> Iniciadas</span>
        <span><span className="inline-block w-3 h-0.5 mr-1 align-middle" style={{ backgroundColor: C.green }} /> Concluídas</span>
      </div>
    </div>
  );
};

// Ícones/cores por hazard_type (alinhado com SafetyWarningModal mobile)
const HAZARD_ICONS = {
  fire: '🔥',
  fire_risk: '⛔',
  wind: '🌬️',
  rain: '🌧️',
  storm: '⛈️',
  fog: '🌫️',
  snow: '❄️',
  heat: '🌡️',
  cold: '🥶',
  coastal: '🌊',
  uv: '☀️',
  other: '⚠️',
};
const SEV_COLOR = { yellow: '#F5C542', orange: '#F08A2B', red: '#E13B3B' };
const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}min`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
};

/**
 * SosPanel — SOS ativos + históricos nas rotas do concelho.
 * Sempre em destaque no topo. Se houver SOS activos, borda vermelha.
 */
const SosPanel = ({ alerts, period, onOpen }) => {
  const [expanded, setExpanded] = React.useState(false);
  const sosList = alerts?.sos || [];
  const activeSos = sosList.filter((s) => !s.resolved_at);
  const resolvedSos = sosList.filter((s) => s.resolved_at);
  const hasActive = activeSos.length > 0;

  // Auto-expande quando há SOS activo
  React.useEffect(() => { if (hasActive) setExpanded(true); }, [hasActive]);

  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{
        backgroundColor: C.cardBg,
        borderColor: hasActive ? '#DC2626' : C.sandLine,
        borderWidth: hasActive ? 2 : 1,
        boxShadow: hasActive ? '0 0 24px rgba(220,38,38,0.35)' : 'none',
      }}
      data-testid="sos-panel"
    >
      <button
        onClick={() => setExpanded((x) => !x)}
        className="w-full flex items-center gap-3 px-4 py-3"
        style={{ background: 'transparent' }}
        data-testid="sos-toggle-btn"
      >
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{
            backgroundColor: hasActive ? 'rgba(220,38,38,0.22)' : 'rgba(0,0,0,0.35)',
            border: `1px solid ${hasActive ? '#DC2626' : C.sandLine}`,
            animation: hasActive ? 'pulseSos 1.4s ease-in-out infinite' : 'none',
          }}
        >
          <span style={{ fontSize: 18 }}>🚨</span>
        </div>
        <div className="flex-1 text-left">
          <div className="text-sm font-black tracking-wider uppercase" style={{ color: hasActive ? '#F87171' : C.ink }}>
            {hasActive ? 'SOS EM CURSO' : 'SOS'}
          </div>
          <div className="text-xs mt-0.5" style={{ color: C.inkDim }}>
            {activeSos.length} ativo{activeSos.length === 1 ? '' : 's'} · {resolvedSos.length} resolvido{resolvedSos.length === 1 ? '' : 's'}
            <span className="ml-2 opacity-70">({period === 'all' ? '30d' : period})</span>
          </div>
        </div>
        {activeSos.length > 0 && (
          <span
            className="px-2 py-1 rounded-full text-xs font-black min-w-[24px] text-center"
            style={{ backgroundColor: '#DC2626', color: '#fff' }}
            data-testid="sos-active-badge"
          >
            {activeSos.length}
          </span>
        )}
        <span className="text-xs" style={{ color: C.inkDim }}>{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 space-y-2" style={{ borderTop: `1px solid ${C.sandLine}` }}>
          {activeSos.length > 0 && (
            <>
              <div className="text-[10px] font-black tracking-widest mt-2 mb-1" style={{ color: '#F87171' }}>
                EM CURSO ({activeSos.length})
              </div>
              {activeSos.slice(0, 10).map((s) => (
                <button
                  key={s.sos_id || s.id}
                  type="button"
                  onClick={() => onOpen?.(s)}
                  className="w-full text-left p-3 rounded flex items-start gap-2 hover:brightness-110 transition"
                  style={{ backgroundColor: 'rgba(220,38,38,0.12)', borderLeft: '4px solid #DC2626' }}
                  data-testid={`sos-item-${s.sos_id || s.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-black" style={{ color: C.ink }}>
                      🚨 {s.user_name || 'Utilizador'} {s.route_name ? `— ${s.route_name}` : ''}
                    </div>
                    <div className="text-[11px] mt-1" style={{ color: C.inkDim }}>
                      {s.message ? `"${s.message}" · ` : ''}
                      {s.lat && s.lon ? `${s.lat.toFixed(4)}, ${s.lon.toFixed(4)} · ` : ''}
                      há {timeAgo(s.created_at)}
                    </div>
                  </div>
                </button>
              ))}
            </>
          )}

          {resolvedSos.length > 0 && (
            <>
              <div className="text-[10px] font-black tracking-widest mt-3 mb-1" style={{ color: C.inkDim }}>
                RESOLVIDOS ({resolvedSos.length})
              </div>
              {resolvedSos.slice(0, 5).map((s) => (
                <button
                  key={s.sos_id || s.id}
                  type="button"
                  onClick={() => onOpen?.(s)}
                  className="w-full text-left p-2.5 rounded flex items-start gap-2 opacity-70 hover:opacity-100 transition"
                  style={{ backgroundColor: 'rgba(0,0,0,0.25)', borderLeft: '3px solid #6B7280' }}
                  data-testid={`sos-resolved-${s.sos_id || s.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold line-through" style={{ color: C.inkDim }}>
                      🚨 {s.user_name || 'Utilizador'} {s.route_name ? `— ${s.route_name}` : ''}
                    </div>
                    <div className="text-[11px] mt-0.5" style={{ color: C.inkDim }}>
                      Resolvido há {timeAgo(s.resolved_at)}
                    </div>
                  </div>
                </button>
              ))}
            </>
          )}

          {sosList.length === 0 && (
            <div className="text-center py-4 text-xs" style={{ color: C.inkDim }}>
              ✅ Sem SOS registados neste período.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * AlertsPanel — hazards oficiais (IPMA/EFFIS) + user_reports.
 * NÃO inclui SOS (esse tem painel dedicado ao lado).
 */
const AlertsPanel = ({ alerts, period, onOpenHazard, onOpenReport }) => {
  const [expanded, setExpanded] = React.useState(false);
  const t = alerts?.totals || {};
  const total = (t.hazards || 0) + (t.user_reports || 0);
  const hazards = alerts?.hazards || [];
  const userReports = alerts?.user_reports || [];

  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{ backgroundColor: C.cardBg, borderColor: total > 0 ? C.brand : C.sandLine }}
      data-testid="alerts-panel"
    >
      <button
        onClick={() => setExpanded((x) => !x)}
        className="w-full flex items-center gap-3 px-4 py-3"
        style={{ background: 'transparent' }}
        data-testid="alerts-toggle-btn"
      >
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: total > 0 ? C.brandDim : 'rgba(0,0,0,0.35)', border: `1px solid ${total > 0 ? C.brand : C.sandLine}` }}
        >
          <span style={{ fontSize: 18 }}>{total > 0 ? '⚠️' : '🛡️'}</span>
        </div>
        <div className="flex-1 text-left">
          <div className="text-sm font-black tracking-wider uppercase" style={{ color: C.ink }}>
            Avisos
          </div>
          <div className="text-xs mt-0.5" style={{ color: C.inkDim }}>
            {(t.hazards || 0)} IPMA/EFFIS · {(t.user_reports || 0)} reports
            <span className="ml-2 opacity-70">({period === 'all' ? '30d' : period})</span>
          </div>
        </div>
        {total > 0 && (
          <span
            className="px-2 py-1 rounded-full text-xs font-black min-w-[24px] text-center"
            style={{ backgroundColor: C.brand, color: '#fff' }}
            data-testid="alerts-total-badge"
          >
            {total}
          </span>
        )}
        <span className="text-xs" style={{ color: C.inkDim }}>{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 space-y-3" style={{ borderTop: `1px solid ${C.sandLine}` }}>
          {/* HAZARDS IPMA/EFFIS */}
          {hazards.length > 0 && (
            <div>
              <div className="text-[10px] font-black tracking-widest mb-1.5" style={{ color: C.brand }}>
                ⚠️ AVISOS OFICIAIS ({hazards.length}) · IPMA · EFFIS
              </div>
              <div className="space-y-1.5">
                {hazards.slice(0, 8).map((h, i) => {
                  const color = SEV_COLOR[h.severity] || SEV_COLOR.yellow;
                  return (
                    <button
                      key={`hz-${i}`}
                      type="button"
                      onClick={() => onOpenHazard?.(h)}
                      className="w-full text-left p-2.5 rounded flex items-start gap-2 hover:brightness-110 transition"
                      style={{ backgroundColor: 'rgba(0,0,0,0.25)', borderLeft: `3px solid ${color}` }}
                      data-testid={`hazard-item-${i}`}
                    >
                      <span style={{ fontSize: 18 }}>{HAZARD_ICONS[h.hazard_type] || '⚠️'}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-bold" style={{ color: C.ink }}>
                          {h.title || h.hazard_type}
                          {h.severity && (
                            <span
                              className="ml-2 px-1.5 py-0.5 rounded text-[9px] font-black uppercase"
                              style={{ backgroundColor: color, color: '#fff' }}
                            >
                              {h.severity}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] mt-0.5" style={{ color: C.inkDim }}>
                          {h.meta?.concelho ? `${h.meta.concelho} · ` : ''}
                          {h.description || h.text || 'Sem descrição'}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* USER REPORTS */}
          {userReports.length > 0 && (
            <div>
              <div className="text-[10px] font-black tracking-widest mb-1.5" style={{ color: C.sand }}>
                👥 REPORTS DOS UTILIZADORES ({userReports.length})
              </div>
              <div className="space-y-1.5">
                {userReports.slice(0, 8).map((r) => (
                  <button
                    key={r.report_id}
                    type="button"
                    onClick={() => onOpenReport?.(r)}
                    className="w-full text-left p-2.5 rounded flex items-start gap-2 hover:brightness-110 transition"
                    style={{ backgroundColor: 'rgba(0,0,0,0.25)', borderLeft: '3px solid #F59E0B' }}
                    data-testid={`report-item-${r.report_id}`}
                  >
                    <span style={{ fontSize: 16 }}>📍</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold" style={{ color: C.ink }}>
                        {r.type || 'obstáculo'}
                        {r.user_name && (
                          <span className="ml-2 font-normal text-[11px]" style={{ color: C.inkDim }}>
                            · {r.user_name}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] mt-0.5" style={{ color: C.inkDim }}>
                        {r.note ? `"${r.note}" · ` : ''}
                        {r.lat && r.lon ? `${r.lat.toFixed(4)}, ${r.lon.toFixed(4)} · ` : ''}
                        há {timeAgo(r.created_at)}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {total === 0 && (
            <div className="text-center py-4 text-xs" style={{ color: C.inkDim }}>
              ✅ Nenhum aviso ativo neste período. Boas condições nas rotas do concelho.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const MunicipioDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading } = useAuth();
  const [period, setPeriod] = useState('30d');
  const [dashboard, setDashboard] = useState(null);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showChart, setShowChart] = useState(false);
  // Rota alvo do RouteViewerModal — abre o mapa+POIs+elevação numa dialog.
  const [viewerRouteId, setViewerRouteId] = useState(null);
  const [alerts, setAlerts] = useState({ hazards: [], user_reports: [], sos: [], totals: {} });
  // Modal de detalhe quando a câmara clica num item da lista SOS/Avisos.
  const [detail, setDetail] = useState(null); // { type, data }

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    // admins sem municipality_id devem usar o dashboard principal — EXCEPTO
    // quando passam `?municipality_id=X` na URL (Fev/2026): permite admin
    // global gerir um município específico via botão "Gerir" no MunicipalitiesManager.
    const url = new URL(window.location.href);
    const overrideMid = user.role === 'admin' && url.searchParams.get('municipality_id');
    if (user.role !== 'municipio' && !user.municipality_id && !overrideMid) {
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate]);

  // Fev/2026 (Ricardo) — admin global pode gerir qualquer município via
  // `?municipality_id=X` na URL. Esse override tem de ser enviado em TODAS
  // as calls API, senão o backend devolve 400 "Admin sem municipality_id".
  const overrideMid = (() => {
    try {
      if (user?.role !== 'admin') return null;
      return new URL(window.location.href).searchParams.get('municipality_id') || null;
    } catch { return null; }
  })();
  const midQS = overrideMid ? `&municipality_id=${encodeURIComponent(overrideMid)}` : '';

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [dashRes, routesRes, alertsRes] = await Promise.all([
        axios.get(`${API}/municipio/dashboard?period=${period}${midQS}`, { withCredentials: true }),
        axios.get(`${API}/municipio/routes?period=${period}${midQS}`, { withCredentials: true }),
        axios.get(
          `${API}/municipio/alerts?period=${period === 'all' ? '30d' : period}${midQS}`,
          { withCredentials: true },
        ),
      ]);
      setDashboard(dashRes.data);
      setRoutes(routesRes.data?.routes || []);
      setAlerts(alertsRes.data || { hazards: [], user_reports: [], sos: [], totals: {} });
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erro a carregar dashboard';
      toast.error(detail);
      if (err.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, user?.user_id]);

  const totals = dashboard?.totals || {};
  const muni = dashboard?.municipality;
  const chart = dashboard?.chart_30d || [];

  const kmFormat = (n) => {
    const v = Number(n) || 0;
    if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
    return v.toFixed(1);
  };

  const doLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Export Excel — usa fetch para respeitar cookies + descarregar como blob.
  const [exporting, setExporting] = useState(false);
  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await axios.get(
        `${API}/municipio/export.xlsx?period=${period}${midQS}`,
        { withCredentials: true, responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      // Nome amigável (backend também envia Content-Disposition)
      const suggested = res.headers['content-disposition']?.match(/filename="([^"]+)"/)?.[1];
      link.setAttribute(
        'download',
        suggested || `tectur_municipio_${muni?.municipality_id || 'export'}_${period}.xlsx`,
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel gerado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro a exportar');
    } finally {
      setExporting(false);
    }
  };

  if (authLoading || (loading && !dashboard)) {
    return (
      <div
        className="min-h-screen w-full flex items-center justify-center"
        style={{ backgroundColor: C.bg, color: C.ink }}
      >
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: C.brand }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full" style={{ backgroundColor: C.bg }} data-testid="municipio-dashboard">
      {/* Header */}
      <header
        className="sticky top-0 z-30 backdrop-blur"
        style={{
          backgroundColor: 'rgba(26,22,19,0.9)',
          borderBottom: `1px solid ${C.sandLine}`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: C.brandDim, border: `1px solid ${C.brand}` }}
            >
              <Building2 className="w-5 h-5" style={{ color: C.brand }} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest font-bold" style={{ color: C.inkDim }}>
                Painel Município
              </div>
              <div className="text-lg font-bold leading-tight" style={{ color: C.ink }}>
                {muni?.name || '—'}
                {muni?.district && (
                  <span className="ml-2 text-sm font-normal" style={{ color: C.inkDim }}>
                    · {muni.district}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Admin global consegue voltar ao Dashboard admin (Ago/2026) */}
            {user?.role === 'admin' && (
              <button
                onClick={() => navigate('/dashboard')}
                className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 transition"
                style={{ backgroundColor: 'rgba(0,0,0,0.35)', color: C.sand, border: `1px solid ${C.sandLine}` }}
                title="Voltar ao dashboard admin global"
                data-testid="muni-back-admin-btn"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Dashboard admin
              </button>
            )}
            {muni?.municipality_id && (
              <>
                <a
                  href={`${window.location.origin}/m/${muni.municipality_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 transition"
                  style={{ color: C.ink, border: `1px solid ${C.sandLine}` }}
                  data-testid="muni-public-page-btn"
                  title="Abrir a página pública do município"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Página pública
                </a>
                <button
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(`${window.location.origin}/m/${muni.municipality_id}`);
                      toast.success('Link da página pública copiado');
                    } catch {
                      toast.error('Não foi possível copiar o link');
                    }
                  }}
                  className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 transition"
                  style={{ color: C.ink, border: `1px solid ${C.sandLine}` }}
                  data-testid="muni-copy-public-link-btn"
                  title="Copiar link para partilhar"
                >
                  <Link2 className="w-3.5 h-3.5" /> Copiar link
                </button>
              </>
            )}
            <button
              onClick={exportExcel}
              disabled={exporting}
              className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 transition disabled:opacity-60"
              style={{ backgroundColor: C.green, color: '#fff' }}
              data-testid="export-excel-btn"
              title="Exportar Excel deste município"
            >
              {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />}
              Excel
            </button>
            <button
              onClick={fetchAll}
              className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 transition"
              style={{ color: C.ink, border: `1px solid ${C.sandLine}` }}
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Atualizar
            </button>
            {/* Voltar ao mapa → volta ao principal com mapa (a sessão continua). */}
            <button
              onClick={() => navigate('/dashboard')}
              className="px-3 py-1.5 rounded-md text-xs font-semibold flex items-center gap-1.5 transition"
              style={{ backgroundColor: C.brand, color: '#fff' }}
              data-testid="muni-exit-btn"
              title="Voltar à página principal com mapa"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Voltar ao mapa
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Period filter */}
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm" style={{ color: C.inkDim }}>
            Período:
          </div>
          <div className="flex gap-1 p-1 rounded-md" style={{ backgroundColor: 'rgba(0,0,0,0.35)', border: `1px solid ${C.sandLine}` }}>
            {PERIODS.map((p) => (
              <button
                key={p.id}
                onClick={() => setPeriod(p.id)}
                data-testid={`period-${p.id}`}
                className="px-3 py-1.5 rounded text-xs font-bold transition"
                style={{
                  backgroundColor: period === p.id ? C.brand : 'transparent',
                  color: period === p.id ? '#fff' : C.ink,
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KpiCard
            icon={RouteIcon}
            label="Rotas do município"
            value={totals.routes ?? 0}
            sub="associadas a este concelho"
          />
          <KpiCard
            icon={TrendingUp}
            label="KM percorridos"
            value={`${kmFormat(totals.total_km || 0)} km`}
            sub={`no período · ${totals.completes ?? 0} sessões`}
          />
          <KpiCard
            icon={Users}
            label="Utilizadores"
            value={totals.unique_users_started ?? 0}
            sub={`${totals.unique_users_completed ?? 0} concluíram`}
            tint={C.sand}
          />
          <KpiCard
            icon={Flag}
            label="Taxa de conclusão"
            value={`${totals.completion_rate ?? 0}%`}
            sub={`${totals.starts ?? 0} iniciadas · ${totals.completes ?? 0} terminadas`}
            tint={C.green}
          />
        </div>

        {/* SOS + Avisos — painéis separados (SOS crítico em destaque) */}
        <SosPanel alerts={alerts} period={period} onOpen={(d) => setDetail({ type: 'sos', data: d })} />
        <AlertsPanel
          alerts={alerts}
          period={period}
          onOpenHazard={(d) => setDetail({ type: 'hazard', data: d })}
          onOpenReport={(d) => setDetail({ type: 'user_report', data: d })}
        />

        {/* Chart toggle */}
        <div
          className="rounded-xl p-4 border"
          style={{ backgroundColor: C.cardBg, borderColor: C.sandLine }}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" style={{ color: C.brand }} />
              <div className="text-sm font-bold uppercase tracking-wider" style={{ color: C.ink }}>
                Utilização últimos 30 dias
              </div>
            </div>
            <button
              onClick={() => setShowChart((v) => !v)}
              className="text-xs px-2.5 py-1 rounded font-semibold"
              style={{ backgroundColor: C.brandDim, color: C.brand, border: `1px solid ${C.brand}` }}
              data-testid="toggle-chart"
            >
              {showChart ? 'Esconder gráfico' : 'Ver gráfico'}
            </button>
          </div>
          {showChart ? (
            <MiniChart data={chart} />
          ) : (
            <div className="text-xs" style={{ color: C.inkDim }}>
              {chart.length === 0
                ? 'Ainda não há sessões registadas nas rotas deste município.'
                : `${chart.reduce((s, d) => s + d.starts, 0)} inícios · ${chart.reduce((s, d) => s + d.completes, 0)} conclusões · ${chart.reduce((s, d) => s + d.km, 0).toFixed(1)} km`}
            </div>
          )}
        </div>

        {/* Tabela de rotas */}
        <div
          className="rounded-xl border overflow-hidden"
          style={{ backgroundColor: C.cardBg, borderColor: C.sandLine }}
          data-testid="routes-table"
        >
          <div className="px-4 py-3 flex items-center gap-2 border-b" style={{ borderColor: C.sandLine }}>
            <RouteIcon className="w-4 h-4" style={{ color: C.brand }} />
            <div className="text-sm font-bold uppercase tracking-wider" style={{ color: C.ink }}>
              Rotas ({routes.length})
            </div>
          </div>
          {routes.length === 0 ? (
            <div className="p-8 text-center text-sm" style={{ color: C.inkDim }}>
              Ainda não há rotas associadas a este município. Pede ao administrador para as associar.
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: C.sandLine }}>
              {routes.map((r) => (
                <div
                  key={r.route_id}
                  className="px-4 py-3 flex items-center justify-between gap-4 hover:bg-black/10"
                  data-testid={`route-row-${r.route_id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate" style={{ color: C.ink }}>
                      {r.name}
                    </div>
                    <div className="text-xs mt-0.5" style={{ color: C.inkDim }}>
                      {r.difficulty ? `${r.difficulty} · ` : ''}
                      {r.distance_km ? `${Number(r.distance_km).toFixed(1)} km` : '—'}
                      {r.region ? ` · ${r.region}` : ''}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 text-right">
                    <div>
                      <div className="text-lg font-bold" style={{ color: C.ink }}>{r.starts}</div>
                      <div className="text-[10px] uppercase" style={{ color: C.inkDim }}>Iniciadas</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold" style={{ color: C.green }}>{r.completes}</div>
                      <div className="text-[10px] uppercase" style={{ color: C.inkDim }}>Concluídas</div>
                    </div>
                    <div>
                      <div className="text-lg font-bold" style={{ color: C.brand }}>{kmFormat(r.total_km)} km</div>
                      <div className="text-[10px] uppercase" style={{ color: C.inkDim }}>Total</div>
                    </div>
                  </div>
                  <button
                    onClick={() => setViewerRouteId(r.route_id)}
                    className="px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-1.5 transition"
                    style={{ backgroundColor: C.brand, color: '#fff' }}
                    title="Visualizar rota no mapa"
                    data-testid={`view-route-${r.route_id}`}
                  >
                    <Eye className="w-3.5 h-3.5" /> Ver rota
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top POIs */}
        <div
          className="rounded-xl border overflow-hidden"
          style={{ backgroundColor: C.cardBg, borderColor: C.sandLine }}
          data-testid="top-pois-table"
        >
          <div className="px-4 py-3 flex items-center gap-2 border-b" style={{ borderColor: C.sandLine }}>
            <MapPin className="w-4 h-4" style={{ color: C.brand }} />
            <div className="text-sm font-bold uppercase tracking-wider" style={{ color: C.ink }}>
              POIs mais clicados
            </div>
          </div>
          {(!dashboard?.top_pois || dashboard.top_pois.length === 0) ? (
            <div className="p-8 text-center text-sm" style={{ color: C.inkDim }}>
              Ainda não há cliques em POIs registados.
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: C.sandLine }}>
              {dashboard.top_pois.map((p, idx) => (
                <div
                  key={`${p.route_id}_${p.poi_idx}`}
                  className="px-4 py-2.5 flex items-center gap-3"
                  data-testid={`poi-row-${p.route_id}-${p.poi_idx}`}
                >
                  <div
                    className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{ backgroundColor: C.brandDim, color: C.brand }}
                  >
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate" style={{ color: C.ink }}>
                      {p.poi_name || `POI #${p.poi_idx}`}
                      {p.poi_type && (
                        <span
                          className="ml-2 text-[10px] px-1.5 py-0.5 rounded uppercase"
                          style={{ backgroundColor: C.brandDim, color: C.brand }}
                        >
                          {p.poi_type}
                        </span>
                      )}
                    </div>
                    <div className="text-xs mt-0.5" style={{ color: C.inkDim }}>
                      Rota: {p.route_name || p.route_id}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold" style={{ color: C.ink }}>{p.clicks}</div>
                    <div className="text-[10px] uppercase" style={{ color: C.inkDim }}>Cliques</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Widget Embed — snippet iframe para colar no site oficial da câmara */}
        {muni?.municipality_id && (
          <EmbedSnippetCard
            slug={muni.municipality_id}
            name={muni.name}
            C={C}
          />
        )}
      </main>

      {/* Modal de visualização da rota — reutiliza RouteMap + ElevationChart
          + POIManager (mesma experiência que o admin tem no dashboard). */}
      {viewerRouteId && (
        <RouteViewerModal
          routeId={viewerRouteId}
          open={!!viewerRouteId}
          onClose={() => setViewerRouteId(null)}
          user={user}
          liveOverlay={true}
        />
      )}

      {/* Modal de detalhe ao clicar num alerta / SOS */}
      <AlertDetailDialog
        open={!!detail}
        onClose={() => setDetail(null)}
        type={detail?.type}
        data={detail?.data}
        municipalityName={dashboard?.municipality?.name}
        onChanged={fetchAll}
      />
    </div>
  );
};

export default MunicipioDashboard;
